import { type Nullable } from './common'
import type { FortuneWheelPrize } from './fortune-wheel'
import { BADGE_ID, DAILY_TASK_ID, SPECIAL_CURRENCY_ID } from './player'
import { PATHWAY_EVENT_STEP_COST_TYPE, type PathwayEvent, type Step, type StepReward } from './pathway-event'

export type UpgradeRequirements = {
  upgrade?: { id: string, level: number }
  referrals?: { count: number }
  newReferrals?: { since: number }
}

export type MilestoneRewards = { goldenCoins: number, spins: number, tickets: number, badgeId?: BADGE_ID }

export type PurchaseReward = {
  minAmountCents: number,
  reward: MilestoneRewards
}
export type TonTxCountReward = {
  minTxCount: number,
  reward: MilestoneRewards
}
export type PathwayCountReward = {
  minCount: number,
  reward: MilestoneRewards
}

export enum SALE_DISCOUNT_TYPE {
  BUY_ONE_GET_TWO = 'buyOneGetTwo',
  BUY_ONE_GET_X = 'buyOneGetX',
  DISCOUNT = 'discount',
  CASHBACK = 'cashback',
}

export enum ECOMMERCE_ITEM_PRICE_TYPE {
  STAR = 'star',
  TON = 'ton',
}

export enum ECOMMERCE_ITEM_BENEFIT_TYPE {
  SPIN = 'spin',
  COIN = 'coin',
  GOLDEN_COIN = 'goldenCoin',
  OFFLINE_PROFIT_MULTIPLIER = 'offlineProfitMultiplier',
  OFFLINE_PROFIT = 'offlineProfit',
  PROFIT_X2 = 'profitX2',
  STAR = 'star',
  VIRTUAL_FRIEND = 'virtualFriend',
  COLLECTIBLE = 'collectible',
  MYSTERY_BOX = 'mysteryBox',
  CASHBACK = 'cashback',
  PATHWAY_STEP = 'pathwayStep',
  SPECIAL_CURRENCY = 'specialCurrency',
  REWARD_LIVE_OP_STEP = 'rewardLiveOp',
  EMBER_ELEMENT_SHARD = 'emberElementShard',
  FROST_ELEMENT_SHARD = 'frostElementShard',
  VOLT_ELEMENT_SHARD = 'voltElementShard',
  STONE_ELEMENT_SHARD = 'stoneElementShard',
  WINTER_CROWN = 'winterCrown',
  PRIME_SIGIL = 'primeSigil',
}

export type MysteryBoxReward = { multiplier?: number } & ({
  type: MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT,
  benefit: EcommerceItemBenefit
} |
  { type: MYSTERY_BOX_REWARD_TYPE.SKIN, category: ECOMMERCE_COLLECTIBLE_CATEGORY } |
  { type: MYSTERY_BOX_REWARD_TYPE.GIVEAWAY_TICKET, amount: number })

export type EcommerceDiscount = {
  type: SALE_DISCOUNT_TYPE.BUY_ONE_GET_TWO
} | {
  type: SALE_DISCOUNT_TYPE.DISCOUNT | SALE_DISCOUNT_TYPE.CASHBACK | SALE_DISCOUNT_TYPE.BUY_ONE_GET_X
  amount: number
}

export type EcommerceConfig = {
  tonPrice: number
  sales: Nullable<{
    start: number
    end: number
    eventName: string
    theme: string
    discount: EcommerceDiscount
  }>
  prices: {
    cardFastForward: {
      ranges: { cooldownUpTo: number, prices: EcommerceItemPrice[] }[]
      minimumCooldown: number
      batchDiscount: number
      batchMinQuantity: number
    }
  }
  items: EcommerceItem[]
  collectibles: Record<string, { type: ECOMMERCE_COLLECTIBLE_TYPE, assetUrl: string }>
  currencies: {
    goldenCoin: number
  }
  mysteryBoxes: Record<MYSTERY_BOX_TYPE, { rewards: { reward: MysteryBoxReward, chance: number }[] }>
  mysteryBoxRewards: Record<MYSTERY_BOX_TYPE, MysteryBoxReward[]>
}

export enum MYSTERY_BOX_REWARD_TYPE {
  ECOMMERCE_BENEFIT = 'ecommerceBenefit',
  SKIN = 'skin',
  GIVEAWAY_TICKET = 'giveawayTicket',
}

export type EcommerceItemPrice = {
  amount: number,
  type: ECOMMERCE_ITEM_PRICE_TYPE,
  usdAmount: number,
  originalUsdAmount: number
}
export type EcommerceItemBenefit =
  {
    amount: number,
    type: Exclude<ECOMMERCE_ITEM_BENEFIT_TYPE, ECOMMERCE_ITEM_BENEFIT_TYPE.COLLECTIBLE | ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX | ECOMMERCE_ITEM_BENEFIT_TYPE.PATHWAY_STEP | ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY | ECOMMERCE_ITEM_BENEFIT_TYPE.REWARD_LIVE_OP_STEP>
  }
  |
  {
    type: ECOMMERCE_ITEM_BENEFIT_TYPE.COLLECTIBLE,
    amount: number,
    collectibleId: string,
    collectibleType: ECOMMERCE_COLLECTIBLE_TYPE,
    category: ECOMMERCE_COLLECTIBLE_CATEGORY
  }
  |
  { type: ECOMMERCE_ITEM_BENEFIT_TYPE.MYSTERY_BOX, amount: number, boxType: MYSTERY_BOX_TYPE }
  |
  { type: ECOMMERCE_ITEM_BENEFIT_TYPE.PATHWAY_STEP, amount: number, eventId: string, stepNumber: number }
  |
  { type: ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY, currency: SPECIAL_CURRENCY_ID, amount: number }
  |
  { type: ECOMMERCE_ITEM_BENEFIT_TYPE.REWARD_LIVE_OP_STEP, amount: number, eventId: number, stepNumber: number }

export enum ECOMMERCE_COLLECTIBLE_TYPE {
  TAP_SKIN = 'tapSkin',
}

export enum ECOMMERCE_COLLECTIBLE_CATEGORY {
  BASIC = 'basic',
  PREMIUM = 'premium',
  EPIC = 'epic',
}

export enum MYSTERY_BOX_TYPE {
  FREE = 'free',
  STARTER = 'starter',
  POWER = 'power',
  MASTER = 'master',
  ELITE = 'elite',
  LEGENDARY = 'legendary',
}

export enum ECOMMERCE_ITEM_TAG {
  STARTER = 'starter',
  AFTER_STARTER = 'afterStarter',
  ONE_TIME = 'oneTime',
  BUY_SPIN = 'buySpin',
  SHOP_BOOSTER = 'shopBooster',
  SHOP_ITEM = 'shopItem',
  CAN_HAVE_DISCOUNT = 'canHaveDiscount',
  NOT_IN_SHOP = 'notInShop',
  CARD_UPGRADE = 'cardUpgrade',
  POPULAR = 'popular',
  BEST_OFFER = 'bestOffer',
  GROUP_SPIN = 'groupSpin',
  GROUP_TPH = 'groupTph',
  GROUP_BUNDLE = 'groupBundle',
  GROUP_OFFLINE_PROFIT = 'groupOfflineProfit',
  GROUP_VIRTUAL_FRIEND = 'groupVirtualFriend',
  GROUP_COLLECTIBLE = 'groupCollectible',
  LIMITED_TIME = 'limitedTime',
  OUT_OF_SPECIAL_CURRENCY = 'outOfSpecialCurrency',
}

export enum SPECIAL_ECOMMERCE_ITEM_ID {
  DAILY_CHECKIN = 'dailyCheckin',
  CARD_UPGRADE = 'cardUpgrade',
  CARD_FAST_FORWARD = 'cardFastForward',
  CARD_FAST_FORWARD_ALL = 'cardFastForwardAll',
  OFFLINE_PROFIT_X4 = 'offlineProfitX4',
  OFFLINE_PROFIT_X20 = 'offlineProfitX20',
  STAR_100 = 'star.100',
  PATHWAY_STEP = 'pathwayStep',
  REWARD_LIVE_OP_STEP = 'rewardLiveOpStep',
}

export type EcommerceItem = {
  id: string
  name: string
  description: string
  trackingId?: string
  prices: EcommerceItemPrice[]
  imageUrl?: string
  isArchived?: boolean
  archivedAt?: number
  multiplier?: number
  canPurchase: boolean
  start?: Nullable<number>
  end?: Nullable<number>
  benefits: EcommerceItemBenefit[]
  tags: ECOMMERCE_ITEM_TAG[]
  limits: {
    time?: { type: 'durationAfterStarterPack', duration: number } | { type: 'afterPurchase', duration: number }
  }
}

export type PlayerUpgrade = {
  id: string
  name: string
  description: string
  price: number
  ecommerce: {
    fullUpgrade: {
      prices: EcommerceItemPrice[]
    }
  }
  profit: number
  level: number
  profitDelta: number
  cooldownUntil: number
  cooldown: number
  section: string
  imageUrl: string
  appearsOn: Nullable<number>
  disappearsOn: Nullable<number>
  expiresOn: Nullable<number>
  highlight: Nullable<{
    cta: Nullable<string>,
    ctaUrl: Nullable<string>,
    description: string,
    logoUrl: string,
    backgroundImageUrl: string
  }>
  requirements: UpgradeRequirements
  createdAt: number
}

export type PlayerTapConfig = {
  tap: {
    level: number
    price: number
    earnPerTap: number
    earnPerTapDelta: number
  }
  energy: {
    level: number
    price: number
    limit: number
    limitDelta: number
  }
}

export enum REWARD_LIVE_OP_CHALLENGE_TYPE {
  PUZZLE = 'puzzle',
}

export type RewardLiveOp = {
  id: number
  image: string
  start: number
  end: number
  challenge: REWARD_LIVE_OP_CHALLENGE_TYPE
  config: {
    steps: Record<string, Step>
    reward: Partial<StepReward>
  }
}

export enum MINI_GAME_ID {
  MEMORY_GAME = 'memory-game',
  MINESWEEPER = 'minesweeper',
}

export enum CHALLENGE_PRIZE_TYPE {
  USDT = 'USDT',
  TON = 'TON',
  NFT = 'NFT',
  BADGE = 'badge',
  SPECIAL_CURRENCY = 'Special currency',
}

export type PrizePoolChallengeCost = {
  type: PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY
  currencyId: SPECIAL_CURRENCY_ID
  amount: number
}

export type GameConfig = {
  profit: {
    offlineHours: number
  },
  pathwayEvents: PathwayEvent[]
  rewardLiveOps: RewardLiveOp[]
  energy: {
    refillPerSecond: number
    limitDelta: number
  },
  tap: {
    earnPerTapDelta: number
  }
  boosters: {
    refill: {
      singleUseCooldown: number
      rechargeCooldown: number
    }
  }
  giveawayStartDate: number
  giveawayEndDate: number
  giveawayPreviousEndDate: number
  giveawayMode: 'ton' | 'nft'
  cardBattle: {
    rewards: { unlockAtLevel: number, rewards: { returnedCost: number, receivesSkin: boolean } }[]
  },
  referrals: {
    giveawayTickets: number
    initialRefereeReward: {
      premium: number
      normal: number
    }
    perLevelReferrerReward: {
      premium: number
      normal: number
      level: number
    }[]
  },
  levels: { from: number, level: number, name: string }[]
  challenges: {
    dailyQuestion: { reward: number }
    dailyCipher: { reward: number, endHour: number }
    dailyCombo: { reward: number, endHour: number }
    dailyUp: DailyUpChallenge
    pathwaysCompletion: PathwaysCompletionChallenge
    memoryGameChallenge: MemoryGameChallenge
    minesweeperGameChallenge: MinesweeperGameChallenge
    puzzleChallenge: PuzzleChallenge
    specialCollectible: SpecialCollectibleChallenge
  }
  tasks: {
    purchase: {
      rewards: PurchaseReward[]
    }
    tonTxCount: {
      rewards: TonTxCountReward[]
    }
    pathwayCount: {
      rewards: PathwayCountReward[]
    }
    others: {
      invite3friends: { reward: number },
      invite5friends: { reward: number },
      connectTonWallet: { reward: number },
      setToken: { reward: number },
    },
    dailyTasks: {
      [DAILY_TASK_ID.TAP]: { reward: number, spins: number, required: number, tickets?: number }
      [DAILY_TASK_ID.UPGRADE]: { reward: number, spins: number, required: number, tickets?: number }
      [DAILY_TASK_ID.POKE]: { reward: number, spins: number, tickets?: number }
      [DAILY_TASK_ID.SPIN_THE_WHEEL]: { reward: number, spins: number, tickets?: number }
      [DAILY_TASK_ID.SHARE_TO_STORY]: { reward: number, spins: number, tickets?: number }
      [DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY]: { reward: number, spins: number, tickets?: number }
      [DAILY_TASK_ID.TON_DAILY_CHECKIN]: { reward: number, spins: number, tickets: number }
      [DAILY_TASK_ID.DAPP_RADAR_TTM]: { reward: number, spins: number, tickets: number }
      [DAILY_TASK_ID.BUY_STARS]: { reward: number, spins: number, required: number, tickets: number }
      [DAILY_TASK_ID.DAILY_PURCHASE]: { reward: number, spins: number, tickets: number }
      [DAILY_TASK_ID.ALL_DONE]: { reward: number, spins: number, tickets?: number }
    }
    dailyCheckin: {
      endHour: number
      rewards: number[]
      rewardsV2: { coins: number, spins?: number }[]
      rewardAfterLastDay: {
        hourProfit: number
      }
    }
  }
  onboarding: {
    reward: number
  }
  ecommerce: EcommerceConfig,
  others: {
    blackFriday: Nullable<{
      startTimestamp: number
      endTimestamp: number
    }>
  },
  ads: {
    others: {
      maxPerDay: number
    }
    doubleOfflineProfit: {
      maxPerDay: number
      multiplier: number
    }
  },
  fortuneWheel: {
    watchAdForDoublePrize: {
      maxPerDay: number
    }
    extraSpin: {
      watchAd: {
        maxPerDay: number
        amount: number
      }
      upgrades: {
        every: number
        amount: number
      }
      referral: {
        amount: number
        maxPerDay: number
        onboarding: number
        levels: { level: number, amount: number }[]
      }
      daily: {
        amount: number
      }
      levelUp: {
        amount: number
      }
    }
    prizes: FortuneWheelPrize[]
    goldenWheel: {
      prizes: FortuneWheelPrize[]
      activation: {
        afterDays: number
      }
    }
  },
  specialCurrencies: {
    dailyExtra: Partial<Record<SPECIAL_CURRENCY_ID, number>>
  }
}

export type PlayerTask = {
  id: string
  title: string
  description: string
  cta: string
  ctaAfterDone: string
  reward: number
  spins: number
  tickets: number
  claimDelay: number
  validations: string[]
  url: string
  imageUrl: string
  categories: string[]
  startsOn: Nullable<number>
  isDone: boolean
  isVerified: boolean
  actionCompletedAt?: number
  targetedCountry: Nullable<string>
  claimedReward: Nullable<number>
}

type DailyUpChallenge = Nullable<{
  prizePool: {
    type: CHALLENGE_PRIZE_TYPE
    amounts: PrizePoolAmounts
  }
}>
type PathwaysCompletionChallenge = Nullable<{
  prizePool: {
    type: CHALLENGE_PRIZE_TYPE
    amounts: PrizePoolAmounts
    from: number
    to: number
  }
}>
type MemoryGameChallenge = Nullable<{
  prizePool: {
    type: CHALLENGE_PRIZE_TYPE
    amounts: PrizePoolAmounts
    from: number
    to: number
    cost: PrizePoolChallengeCost
  }
}>
type MinesweeperGameChallenge = Nullable<{
  prizePool: {
    type: CHALLENGE_PRIZE_TYPE
    amounts: PrizePoolAmounts
    from: number
    to: number
    cost: PrizePoolChallengeCost
  }
}>
type PuzzleChallenge = Nullable<{
  prizePool: {
    type: CHALLENGE_PRIZE_TYPE
    amounts: PrizePoolAmounts
    from: number
    to: number
  }
}>
type SpecialCollectibleChallenge = Nullable<{
  prizePool: {
    type: CHALLENGE_PRIZE_TYPE
    amounts: PrizePoolAmounts
    from: number
    to: number
    currency: SPECIAL_CURRENCY_ID
    challengeName: string | undefined
  }
}>

export type Challenge =
  | DailyUpChallenge
  | PathwaysCompletionChallenge
  | MemoryGameChallenge
  | MinesweeperGameChallenge
  | PuzzleChallenge
  | SpecialCollectibleChallenge

export type PlayerConfig = {
  referrals: {
    count: number
  }
  upgrades: PlayerUpgrade[]
  upgradeSections: string[]
  tap: PlayerTapConfig
  game: GameConfig
  tasks: PlayerTask[]
}

export type PrizePoolBase = { fromRank: number, toRank: number, badgeId?: BADGE_ID }
export type PrizePoolCash = PrizePoolBase & { amount: number }
export type PrizePoolSpecialCurrency = PrizePoolBase & { amount: number, specialCurrencyId: SPECIAL_CURRENCY_ID }
export type PrizePoolNft = PrizePoolBase & { address: string, nft: string, lottieUrl: string }

export type PrizePoolAmount = (PrizePoolCash | PrizePoolSpecialCurrency | PrizePoolNft)
export type PrizePoolAmounts = PrizePoolAmount[]

export function isPrizePoolCash(prize?: PrizePoolAmount): prize is PrizePoolCash {
  if (!prize) return false
  return 'amount' in prize && !('specialCurrencyId' in prize)
}

export function isPrizePoolSpecialCurrency(prize: PrizePoolAmount): prize is PrizePoolSpecialCurrency {
  return 'specialCurrencyId' in prize
}

export function isPrizePoolNft(prize?: PrizePoolAmount): prize is PrizePoolNft {
  if (!prize) return false
  return 'nft' in prize
}
